import React from 'react'
import { Box, Checkbox, Field, Flex, Input } from '@workwhile/ui'
import { FieldError, FieldErrors } from 'react-hook-form'
import { FullOptions } from 'fast-fuzzy'
import {
  CompanyScopeGrantFormSchema,
  UserPermissionsFormSchema,
} from '../../schema'
import { getDefaultEffectiveAt } from '../PermissionsChecklist'

export type ScopeOption = { label: string; value: number }

export const scopeFuzzyOptions: FullOptions<ScopeOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

export type ExistingCompanyScope = {
  allCompanies: boolean
  companyId: number | null
  companyGroupId: number | null
}

export type CompanyScopesEditorProps = {
  value: CompanyScopeGrantFormSchema[]
  onChange: (scopes: CompanyScopeGrantFormSchema[]) => void
  existingScopes?: ExistingCompanyScope[]
  errors?: FieldErrors<UserPermissionsFormSchema>['companyScopes']
}

export function createScopeDates(): Pick<
  CompanyScopeGrantFormSchema,
  'effectiveAt' | 'expiresAt'
> {
  return {
    effectiveAt: getDefaultEffectiveAt(),
    expiresAt: null,
  }
}

export const ScopeDatesFields = ({
  scope,
  index,
  errors,
  onChange,
}: {
  scope: CompanyScopeGrantFormSchema
  index: number
  errors?: FieldErrors<UserPermissionsFormSchema>['companyScopes']
  onChange: (next: CompanyScopeGrantFormSchema) => void
}) => {
  const itemErrors = Array.isArray(errors) ? errors[index] : undefined
  const effectiveAtError = (
    itemErrors?.effectiveAt as FieldError | undefined
  )?.message?.toString()
  const expiresAtError = (
    itemErrors?.expiresAt as FieldError | undefined
  )?.message?.toString()
  const neverExpires = scope.expiresAt == null

  return (
    <Flex mt={3} gap={3} flexDirection={['column', 'row']}>
      <Box flex={1}>
        <Field label="Effective at" error={effectiveAtError}>
          <Input
            type="datetime-local"
            step={60}
            value={scope.effectiveAt}
            onChange={(event) =>
              onChange({ ...scope, effectiveAt: event.target.value })
            }
            aria-label="Company scope effective at"
          />
        </Field>
      </Box>
      <Box flex={1}>
        <Field label="Expires at" error={expiresAtError}>
          <Flex flexDirection="column" gap={2}>
            <Input
              type="datetime-local"
              step={60}
              value={scope.expiresAt ?? ''}
              disabled={neverExpires}
              onChange={(event) =>
                onChange({
                  ...scope,
                  expiresAt:
                    event.target.value === '' ? null : event.target.value,
                })
              }
              aria-label="Company scope expires at"
            />
            <Checkbox
              value={neverExpires}
              onChange={(checked) =>
                onChange({
                  ...scope,
                  expiresAt: checked
                    ? null
                    : scope.effectiveAt || getDefaultEffectiveAt(),
                })
              }
            >
              Never expires
            </Checkbox>
          </Flex>
        </Field>
      </Box>
    </Flex>
  )
}

export function updateScopeAt(
  value: CompanyScopeGrantFormSchema[],
  index: number,
  next: CompanyScopeGrantFormSchema
): CompanyScopeGrantFormSchema[] {
  return value.map((scope, i) => (i === index ? next : scope))
}

export function removeScopeAt(
  value: CompanyScopeGrantFormSchema[],
  index: number
): CompanyScopeGrantFormSchema[] {
  return value.filter((_, i) => i !== index)
}
